import {
  FC,
  ReactNode,
  PointerEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Box, Container, Fade, Modal } from '@mui/material'
import { GRAY_COLORS_VARIANTS, TEXT_DARK_COLORS_VARIANTS } from '@/utils/constants/colors'

const DRAG_THRESHOLD = 80

// Sheets can stack (e.g. top-up opened over enter-draw), so the lock is ref-counted and only
// released when the last open sheet closes. Pinning the body with position: fixed is used
// instead of overflow: hidden because iOS Safari still scrolls the page with the latter.
let scrollLockCount = 0
let lockedScrollY = 0

const lockBodyScroll = () => {
  scrollLockCount += 1
  if (scrollLockCount > 1) return
  lockedScrollY = window.scrollY
  Object.assign(document.body.style, {
    position: 'fixed',
    top: `-${lockedScrollY}px`,
    left: '0',
    right: '0',
    width: '100%',
  })
}

const unlockBodyScroll = () => {
  scrollLockCount = Math.max(0, scrollLockCount - 1)
  if (scrollLockCount > 0) return
  Object.assign(document.body.style, { position: '', top: '', left: '', right: '', width: '' })
  window.scrollTo({ top: lockedScrollY, behavior: 'instant' as ScrollBehavior })
}

export interface BottomSheetProps {
  open: boolean
  onClose: () => void
  children: ReactNode
  zIndex?: number
}

export const BottomSheet: FC<BottomSheetProps> = ({ open, onClose, children, zIndex = 1300 }) => {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => setVisible(true))
    } else {
      setVisible(false)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    lockBodyScroll()
    return unlockBodyScroll
  }, [open])

  const sheetRef = useRef<HTMLDivElement>(null)
  const startYRef = useRef<number>(0)
  const currentDeltaRef = useRef<number>(0)
  const [isDragging, setIsDragging] = useState(false)
  const [dragDelta, setDragDelta] = useState(0)

  const onPointerDown = useCallback((e: PointerEvent<HTMLDivElement>) => {
    startYRef.current = e.clientY
    currentDeltaRef.current = 0
    setIsDragging(true)
    try {
      ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
    } catch (err) {
      // Ignore if setPointerCapture fails
    }
  }, [])

  const onPointerMove = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if (!isDragging) return
      const delta = Math.max(0, e.clientY - startYRef.current)
      currentDeltaRef.current = delta
      setDragDelta(delta)
    },
    [isDragging],
  )

  const onPointerUp = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      setIsDragging(false)
      if (currentDeltaRef.current >= DRAG_THRESHOLD) {
        setDragDelta(0)
        onClose()
      } else {
        setDragDelta(0)
      }
      try {
        ;(e.target as HTMLElement).releasePointerCapture(e.pointerId)
      } catch (err) {
        // Ignore
      }
    },
    [onClose],
  )

  const onClickCapture = useCallback((e: MouseEvent<HTMLDivElement>) => {
    if (currentDeltaRef.current > 5) {
      e.stopPropagation()
      e.preventDefault()
    }
  }, [])

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      hideBackdrop
      disableScrollLock // MUI locks with overflow: hidden, which iOS Safari ignores
      sx={{ zIndex }}
    >
      {/* Exit matches the sheet's 320ms slide-down so it finishes before the Modal unmounts */}
      <Fade in={open} timeout={{ enter: 200, exit: 320 }}>
        <Box
          position="fixed"
          display="flex"
          alignItems="flex-end"
          bgcolor={`${TEXT_DARK_COLORS_VARIANTS[500]}33`}
          sx={{
            inset: 0,
            outline: 'none',
            backdropFilter: 'blur(2px)',
            WebkitBackdropFilter: 'blur(2px)',
          }}
          onClick={onClose}
        >
          <Container
            ref={sheetRef}
            role="dialog"
            aria-modal="true"
            maxWidth="lg"
            onClick={(e) => e.stopPropagation()}
            onClickCapture={onClickCapture}
            sx={{
              p: '0px !important',
              bgcolor: 'white',
              borderRadius: '1.25rem 1.25rem 0 0',
              // Leave a strip of backdrop above the sheet so it can still be tapped to close
              maxHeight: 'calc(100dvh - 2.5rem)',
              overflowY: 'auto',
              overscrollBehavior: 'contain',
              WebkitOverflowScrolling: 'touch',
              boxShadow: '0 -8px 40px rgba(0,0,0,0.18)',
              transform: visible ? `translateY(${dragDelta}px)` : 'translateY(100%)',
              transition: isDragging ? 'none' : 'transform 0.32s cubic-bezier(0.32,0.72,0,1)',
              willChange: 'transform',
              position: 'relative',
              pb: 'env(safe-area-inset-bottom) !important',
            }}
          >
            <Box
              position="absolute"
              top={0}
              left="50%"
              display="flex"
              justifyContent="center"
              pt={1.25}
              pb={0.5}
              zIndex={1}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
              sx={{
                transform: 'translateX(-50%)',
                cursor: 'grab',
                touchAction: 'none',
                userSelect: 'none',
                '&:active': { cursor: 'grabbing' },
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 4,
                  borderRadius: '999px',
                  bgcolor: GRAY_COLORS_VARIANTS[100],
                }}
              />
            </Box>

            {children}
          </Container>
        </Box>
      </Fade>
    </Modal>
  )
}
